import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Close as CloseIcon,
  UnfoldLess as CollapseIcon,
  DragHandle as DragHandleIcon,
  UnfoldMore as ExpandIcon,
} from '@mui/icons-material';
import { Box, IconButton, Paper, Tab, Tabs, Tooltip } from '@mui/material';

import { useRecipeEditor } from '../context/RecipeEditorContext';
import { EventContextPanel } from './EventContextPanel';
import { ExecutionLogPanel } from './ExecutionLogPanel';
import { RunningStatusPanel } from './RunningStatusPanel';

const DEFAULT_HEIGHT = 400;
const MIN_HEIGHT = 120;
const TAB_BAR_HEIGHT = 36;

interface BottomPanelProps {
  tenantId: string;
  environments: Array<{ id: string; name: string }>;
  tenantKey: string;
  appUniqueKey: string;
}

/**
 * Tabbed bottom panel matching legacy dataProtect behavior.
 * Renders closeable tabs for: Recipe Running / Results, Event Context, Execution Log.
 * Supports drag-to-resize (top edge) and expand/collapse, matching legacy re-resizable behavior.
 */
export const BottomPanel: React.FC<BottomPanelProps> = ({
  tenantId,
  environments,
  tenantKey,
  appUniqueKey,
}) => {
  const { state, dispatch } = useRecipeEditor();
  const { bottomPanelTabs, activeBottomTabId } = state;

  const [panelHeight, setPanelHeight] = useState(DEFAULT_HEIGHT);
  const [isExpanded, setIsExpanded] = useState(false);
  const [heightBeforeExpand, setHeightBeforeExpand] = useState(DEFAULT_HEIGHT);

  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);

  const getMaxHeight = useCallback(() => {
    return Math.floor(window.innerHeight * 0.85);
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      startYRef.current = e.clientY;
      startHeightRef.current = isExpanded ? getMaxHeight() : panelHeight;
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    },
    [panelHeight, isExpanded, getMaxHeight]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = startYRef.current - e.clientY;
      const newHeight = Math.max(
        MIN_HEIGHT,
        Math.min(startHeightRef.current + delta, getMaxHeight())
      );
      setPanelHeight(newHeight);
      if (isExpanded) setIsExpanded(false);
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isExpanded, getMaxHeight]);

  const toggleExpand = useCallback(() => {
    if (isExpanded) {
      setPanelHeight(heightBeforeExpand);
      setIsExpanded(false);
    } else {
      setHeightBeforeExpand(panelHeight);
      setIsExpanded(true);
    }
  }, [isExpanded, panelHeight, heightBeforeExpand]);

  if (bottomPanelTabs.length === 0) return null;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    dispatch({ type: 'SET_ACTIVE_BOTTOM_TAB', payload: newValue });
  };

  const handleCloseTab = (tabId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch({ type: 'REMOVE_BOTTOM_TAB', payload: tabId });
  };

  const activeTab = bottomPanelTabs.find((t) => t.id === activeBottomTabId) || bottomPanelTabs[0];

  const renderTabContent = () => {
    if (!activeTab) return null;

    switch (activeTab.type) {
      case 'runningStatus':
        return <RunningStatusPanel tenantId={tenantId} />;
      case 'eventContext':
        return (
          <EventContextPanel
            tenantId={tenantId}
            environments={environments}
            tenantKey={tenantKey}
            appUniqueKey={appUniqueKey}
          />
        );
      case 'executionLog':
        return <ExecutionLogPanel tenantId={tenantId} />;
      default:
        return null;
    }
  };

  const effectiveHeight = isExpanded ? getMaxHeight() : panelHeight;

  return (
    <Paper
      ref={panelRef}
      elevation={8}
      sx={{
        height: effectiveHeight,
        display: 'flex',
        flexDirection: 'column',
        borderTop: 2,
        borderColor: 'divider',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {/* Drag handle — top edge resize */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          top: -4,
          left: 0,
          right: 0,
          height: 8,
          cursor: 'ns-resize',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover > .drag-indicator': { opacity: 1 },
        }}
      >
        <DragHandleIcon
          className="drag-indicator"
          sx={{
            fontSize: 16,
            color: 'text.disabled',
            opacity: 0,
            transition: 'opacity 0.15s',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Tab bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Tabs
          value={activeTab?.id || false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            minHeight: TAB_BAR_HEIGHT,
            '& .MuiTab-root': {
              minHeight: TAB_BAR_HEIGHT,
              py: 0,
              px: 2,
              fontSize: 13,
              textTransform: 'none',
            },
          }}
        >
          {bottomPanelTabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{tab.title}</span>
                  {tab.closeable && (
                    <Box
                      component="span"
                      role="button"
                      tabIndex={-1}
                      onClick={(e: React.MouseEvent) => handleCloseTab(tab.id, e)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ml: 0.5,
                        p: 0.25,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        '& svg': { fontSize: 14 },
                      }}
                    >
                      <CloseIcon />
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>

        {/* Expand / Collapse toggle */}
        <Tooltip title={isExpanded ? 'Collapse panel' : 'Expand panel'}>
          <IconButton size="small" onClick={toggleExpand} sx={{ mr: 0.5 }}>
            {isExpanded ? (
              <CollapseIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>{renderTabContent()}</Box>
    </Paper>
  );
};
